// FAQCategories.tsx (component file)
import React from 'react';
import {
    Container,
    Header,
    FAQItem,
    Title,
    Description,
    HeaderTitle,
    IconContainer,
    HeaderSubtitle,
    BottomSection,
    TopSection,
} from './faq-categories.styles';
import MaterialIcon from '../../../../components/material-icon';
import { colorPalette } from '../../../../utils/theme';
import CTAButton from '../../../../components/cta-button/cta-button';
import useIsMobile from '../../../../hooks/useIsMobile';
import useCustomNavigate from '../../../../hooks/useCustomNavigate';
import { MaterialSymbol } from '@material-symbols/font-400';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

export interface FAQCategoryItem {
    id: number;
    title: string;
    description: string;
    icon: MaterialSymbol;
}

export const FAQ_CATEGORIES = (t: TFunction): FAQCategoryItem[] => [
    {
        id: 1,
        title: t("pages.knowledge_hub.faq_categories.faq_category_1.title"),
        description: t("pages.knowledge_hub.faq_categories.faq_category_1.description"),
        icon: "in_home_mode"
    },
    {
        id: 2,
        title: t("pages.knowledge_hub.faq_categories.faq_category_2.title"),
        description: t("pages.knowledge_hub.faq_categories.faq_category_2.description"),
        icon: "key"
    },
    {
        id: 3,
        title: t("pages.knowledge_hub.faq_categories.faq_category_3.title"),
        description: t("pages.knowledge_hub.faq_categories.faq_category_3.description"),
        icon: "real_estate_agent"
    },
    {
        id: 4,
        title: t("pages.knowledge_hub.faq_categories.faq_category_4.title"),
        description: t("pages.knowledge_hub.faq_categories.faq_category_4.description"),
        icon: "location_away"
    },
];

interface FAQCategoriesProps {
    isArticlesVisible: boolean;
}

const FAQCategories: React.FC<FAQCategoriesProps> = ({ isArticlesVisible }) => {
    const { t } = useTranslation();
    const navigate = useCustomNavigate();
    const isMobile = useIsMobile();

    const goToFAQWithCategory = (categoryId: number) => {
        navigate(`/faq?categoryId=${categoryId}`);
    };

    const renderHeader = (
        <>
            {isMobile ? (
                <Header>
                    <div className="d-flex align-items-center" style={{ gap: 4 }} onClick={isMobile ? () => navigate("/faq") : undefined}>
                        <HeaderTitle>{t("pages.knowledge_hub.faq_categories.title")}</HeaderTitle>
                        <MaterialIcon icon="chevron_forward" size={24} color={colorPalette.textMain} />
                    </div>
                    <HeaderSubtitle>{t("pages.knowledge_hub.faq_categories.description")}</HeaderSubtitle>
                </Header>
            ) : (
                <Header>
                    <div className="d-flex align-items-center justify-content-between" onClick={isMobile ? () => navigate("/faq") : undefined}>
                        <HeaderTitle>{t("pages.knowledge_hub.faq_categories.title")}</HeaderTitle>
                        <CTAButton
                            variant="ghost"
                            size="S"
                            label={t("pages.knowledge_hub.faq_categories.show_all_questions")}
                            icon="arrow_outward"
                            onClick={() => navigate("/faq")}
                            noPadding
                        />
                    </div>
                    <HeaderSubtitle>{t("pages.knowledge_hub.faq_categories.description")}</HeaderSubtitle>
                </Header>
            )}
        </>
    );

    return (
        <Container>
            {renderHeader}
            <Row className={isMobile ? "gy-2 gx-2" : "gy-3 gx-3"}>
                {FAQ_CATEGORIES(t).map((category) => (
                    <Col xs={6} md={isArticlesVisible ? 4 : 6} xl={isArticlesVisible ? 3 : 6} key={category.id}>
                        <FAQItem onClick={() => goToFAQWithCategory(category.id)}>
                            <TopSection>
                                <IconContainer>
                                    <MaterialIcon icon={category.icon} size={36} color={colorPalette.textMain} />
                                </IconContainer>
                            </TopSection>
                            <BottomSection>
                                <Title>{category.title}</Title>
                                <Description>{category.description}</Description>
                            </BottomSection>
                        </FAQItem>
                    </Col>
                ))}
            </Row>
        </Container>
    );
};

export default FAQCategories;
